import PropTypes from 'prop-types';
import { CheckboxGroupInput, required, SelectInput, TextInput, useTranslate } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { forwarderTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { operatorChoices } from '@/constants/Operators.js';
import { ContractorEditServiceCompanies } from '@/components/contractors/ContractorEditServiceCompanies.jsx';
import { DepositWithdrawalMethodsField, SettingsCheckboxGroup, SettingsDiscountRanges, SettingsSection } from '@/components/global/forms/SettingsSection.jsx';

const CONFIGURATION_OPERATOR_IDS = new Set(['Zwrotka', 'Kaucja', 'PSK', 'Reselekt']);

const couponSourceChoices = [
    { id: 'comp', name: 'Comp' },
    { id: 'zwrotka', name: 'Zwrotka' },
    { id: 'eurocash', name: 'Eurocash' },
    { id: 'maasloop', name: 'MaasLoop' },
];

const operatorOptions = operatorChoices.filter(choice => CONFIGURATION_OPERATOR_IDS.has(choice.id));

export const ConfigurationSettingsFields = ({
    title,
    fieldLabel,
    operatorsValidate,
    forwarderValidate,
    methodsValidate,
    validateCurrency,
    validateDigits,
    limitTitle,
    valueTitle,
    limitLabelKey,
    valueLabelKey,
    serviceCompanyProps = {},
}) => {
    const translate = useTranslate();
    const requiredField = required(translate('common.validation.required'));

    return (
        <SettingsSection title={title}>
            <Box className='settings-split'>
                <SettingsCheckboxGroup>
                    <CheckboxGroupInput
                        source='operators'
                        label={fieldLabel('operators')}
                        choices={operatorOptions}
                        validate={operatorsValidate}
                        defaultValue={[]}
                        row={false}
                    />
                </SettingsCheckboxGroup>
                <DepositWithdrawalMethodsField label={fieldLabel('depositWithdrawalMethods')} validate={methodsValidate} defaultValue={['cash']} />
            </Box>
            <Box className='settings-split settings-split--compact'>
                <SelectInput
                    source='forwarderType'
                    label={fieldLabel('forwarderType')}
                    choices={forwarderTypeChoices(translate)}
                    defaultValue='Default'
                    validate={forwarderValidate}
                    fullWidth
                />
                <SelectInput
                    source='couponSource'
                    label={fieldLabel('couponSource')}
                    choices={couponSourceChoices}
                    emptyText={translate('common.empty')}
                    validate={requiredField}
                    fullWidth
                />
            </Box>
            <Typography variant='h6' gutterBottom>
                {translate('resources.contractor.labels.defaultValuesForOnePackage')}
            </Typography>
            <Box className='settings-values'>
                <TextInput source='defaultDepositValue' label={fieldLabel('defaultDepositValue')} validate={validateCurrency} fullWidth />
                <TextInput source='defaultDepositPoints' label={fieldLabel('defaultDepositPoints')} validate={validateDigits} fullWidth />
            </Box>
            <SettingsDiscountRanges
                InputField={TextInput}
                limitTitle={limitTitle}
                valueTitle={valueTitle}
                limitLabelKey={limitLabelKey}
                valueLabelKey={valueLabelKey}
                validate={validateDigits}
            />
            <ContractorEditServiceCompanies {...serviceCompanyProps} />
        </SettingsSection>
    );
};

ConfigurationSettingsFields.propTypes = {
    title: PropTypes.string.isRequired,
    fieldLabel: PropTypes.func.isRequired,
    operatorsValidate: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
    forwarderValidate: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
    methodsValidate: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
    validateCurrency: PropTypes.func,
    validateDigits: PropTypes.func,
    limitTitle: PropTypes.string.isRequired,
    valueTitle: PropTypes.string.isRequired,
    limitLabelKey: PropTypes.string.isRequired,
    valueLabelKey: PropTypes.string.isRequired,
    serviceCompanyProps: PropTypes.object,
};
